import React, { createContext, useState, useEffect } from 'react';

interface RecentlyCreatedContextType {
  recentlyCreatedDevices: string[];
  recentlyCreatedSeniors: string[];
  recentlyCreatedCaregivers: string[];
  addRecentlyCreatedDevice: (deviceId: string) => void;
  addRecentlyCreatedSenior: (seniorId: string) => void;
  addRecentlyCreatedCaregiver: (caregiverId: string) => void;
  clearRecentlyCreated: () => void;
}

const RecentlyCreatedContext = createContext<
  RecentlyCreatedContextType | undefined
>(undefined);

export { RecentlyCreatedContext };

export const RecentlyCreatedProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [recentlyCreatedDevices, setRecentlyCreatedDevices] = useState<
    string[]
  >([]);
  const [recentlyCreatedSeniors, setRecentlyCreatedSeniors] = useState<
    string[]
  >([]);
  const [recentlyCreatedCaregivers, setRecentlyCreatedCaregivers] = useState<
    string[]
  >([]);

  // Load from localStorage on mount
  useEffect(() => {
    const savedDevices = localStorage.getItem('recentlyCreatedDevices');
    const savedSeniors = localStorage.getItem('recentlyCreatedSeniors');
    const savedCaregivers = localStorage.getItem('recentlyCreatedCaregivers');

    if (savedDevices) {
      setRecentlyCreatedDevices(JSON.parse(savedDevices));
    }
    if (savedSeniors) {
      setRecentlyCreatedSeniors(JSON.parse(savedSeniors));
    }
    if (savedCaregivers) {
      setRecentlyCreatedCaregivers(JSON.parse(savedCaregivers));
    }
  }, []);

  const addRecentlyCreatedDevice = (deviceId: string) => {
    const updated = [
      deviceId,
      ...recentlyCreatedDevices.filter(id => id !== deviceId),
    ];
    setRecentlyCreatedDevices(updated);
    localStorage.setItem('recentlyCreatedDevices', JSON.stringify(updated));
  };

  const addRecentlyCreatedSenior = (seniorId: string) => {
    const updated = [
      seniorId,
      ...recentlyCreatedSeniors.filter(id => id !== seniorId),
    ];
    setRecentlyCreatedSeniors(updated);
    localStorage.setItem('recentlyCreatedSeniors', JSON.stringify(updated));
  };

  const addRecentlyCreatedCaregiver = (caregiverId: string) => {
    const updated = [
      caregiverId,
      ...recentlyCreatedCaregivers.filter(id => id !== caregiverId),
    ];
    setRecentlyCreatedCaregivers(updated);
    localStorage.setItem('recentlyCreatedCaregivers', JSON.stringify(updated));
  };

  const clearRecentlyCreated = () => {
    setRecentlyCreatedDevices([]);
    setRecentlyCreatedSeniors([]);
    setRecentlyCreatedCaregivers([]);
    localStorage.removeItem('recentlyCreatedDevices');
    localStorage.removeItem('recentlyCreatedSeniors');
    localStorage.removeItem('recentlyCreatedCaregivers');
  };

  return (
    <RecentlyCreatedContext.Provider
      value={{
        recentlyCreatedDevices,
        recentlyCreatedSeniors,
        recentlyCreatedCaregivers,
        addRecentlyCreatedDevice,
        addRecentlyCreatedSenior,
        addRecentlyCreatedCaregiver,
        clearRecentlyCreated,
      }}
    >
      {children}
    </RecentlyCreatedContext.Provider>
  );
};
